import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Loader2, Star } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { inr } from "@/lib/format";
import AdminLayout from "./AdminLayout";

const EMPTY = {
  title: "", category: "hidden-places", location: "", region: "", description: "", long_description: "",
  image: "", gallery: "", price: 0, duration: "", mode: "bookable", difficulty: "Easy",
  highlights: "", rating: 4.8, featured: false, active: true, max_guests: 12,
};

export default function AdminExperiences() {
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = () => { setLoading(true); api.get("/admin/experiences").then((r) => setItems(r.data)).finally(() => setLoading(false)); };
  useEffect(() => { load(); api.get("/categories").then((r) => setCats(r.data)).catch(() => {}); }, []);

  const openNew = () => { setForm(EMPTY); setEditing("new"); };
  const openEdit = (e) => setEditing(e.id) || setForm({ ...EMPTY, ...e, gallery: (e.gallery || []).join(", "), highlights: (e.highlights || []).join(", ") });

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        ...form, price: Number(form.price), rating: Number(form.rating), max_guests: Number(form.max_guests),
        gallery: form.gallery ? form.gallery.split(",").map((s) => s.trim()).filter(Boolean) : [],
        highlights: form.highlights ? form.highlights.split(",").map((s) => s.trim()).filter(Boolean) : [],
      };
      if (editing === "new") await api.post("/admin/experiences", payload);
      else await api.put(`/admin/experiences/${editing}`, payload);
      toast.success("Experience saved"); setEditing(null); load();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const remove = async (e) => {
    if (!window.confirm(`Delete "${e.title}"?`)) return;
    try { await api.delete(`/admin/experiences/${e.id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(apiErr(err)); }
  };

  return (
    <AdminLayout title="Experiences">
      <div className="flex justify-between items-center mb-6">
        <p className="text-muted-foreground text-sm">{items.length} experiences</p>
        <button onClick={openNew} className="inline-flex items-center gap-2 rounded-md bg-primary text-primary-foreground px-4 py-2 text-sm font-semibold" data-testid="add-experience"><Plus size={16} /> New experience</button>
      </div>

      {loading ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">{[...Array(6)].map((_, i) => <div key={i} className="h-44 rounded-md bg-secondary/60 animate-pulse" />)}</div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3" data-testid="admin-experiences-grid">
          {items.map((e) => (
            <div key={e.id} className="rounded-md border border-primary/10 bg-card overflow-hidden" data-testid={`admin-exp-${e.id}`}>
              <img src={e.image} alt={e.title} className="h-28 w-full object-cover" />
              <div className="p-4">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs font-semibold uppercase text-accent">{e.category_label}</span>
                  <span className="text-xs text-muted-foreground flex items-center gap-1">{e.mode}{!e.active ? " · hidden" : ""}{e.featured ? " · ★" : ""}</span>
                </div>
                <h3 className="font-semibold text-foreground mt-1">{e.title}</h3>
                <p className="text-sm text-muted-foreground">{inr(e.price)} · <Star size={11} className="inline fill-accent text-accent" /> {e.rating}</p>
                <div className="flex gap-2 mt-3">
                  <button onClick={() => openEdit(e)} className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-md border border-primary/15 py-2 text-sm hover:bg-secondary" data-testid={`edit-${e.id}`}><Pencil size={14} /> Edit</button>
                  <button onClick={() => remove(e)} className="grid place-items-center w-9 rounded-md border border-primary/15 text-destructive hover:bg-destructive/10" data-testid={`delete-${e.id}`}><Trash2 size={14} /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-primary/40 backdrop-blur-sm p-4 overflow-y-auto" data-testid="experience-modal">
          <div className="w-full max-w-2xl rounded-xl bg-card border border-primary/10 p-6 my-8">
            <div className="flex items-center justify-between mb-5"><h3 className="font-semibold text-lg text-foreground">{editing === "new" ? "New experience" : "Edit experience"}</h3><button onClick={() => setEditing(null)}><X size={18} /></button></div>
            <div className="grid sm:grid-cols-2 gap-3">
              <Input label="Title" value={form.title} onChange={(v) => setForm({ ...form, title: v })} testid="exp-title" wide />
              <Sel label="Category" value={form.category} onChange={(v) => setForm({ ...form, category: v })} testid="exp-category" options={cats.map((c) => [c.slug, c.label])} />
              <Input label="Location" value={form.location} onChange={(v) => setForm({ ...form, location: v })} testid="exp-location" />
              <Input label="Region" value={form.region} onChange={(v) => setForm({ ...form, region: v })} testid="exp-region" />
              <Input label="Duration" value={form.duration} onChange={(v) => setForm({ ...form, duration: v })} testid="exp-duration" />
              <Input label="Difficulty" value={form.difficulty} onChange={(v) => setForm({ ...form, difficulty: v })} testid="exp-difficulty" />
              <Input label="Price (₹)" type="number" value={form.price} onChange={(v) => setForm({ ...form, price: v })} testid="exp-price" />
              <Input label="Rating" type="number" value={form.rating} onChange={(v) => setForm({ ...form, rating: v })} testid="exp-rating" />
              <Sel label="Mode" value={form.mode} onChange={(v) => setForm({ ...form, mode: v })} testid="exp-mode" options={[["bookable", "Bookable"], ["discovery", "Discovery / Enquiry"]]} />
              <Input label="Max guests" type="number" value={form.max_guests} onChange={(v) => setForm({ ...form, max_guests: v })} testid="exp-maxguests" />
              <Input label="Image URL" value={form.image} onChange={(v) => setForm({ ...form, image: v })} testid="exp-image" wide />
              <Input label="Gallery URLs (comma separated)" value={form.gallery} onChange={(v) => setForm({ ...form, gallery: v })} testid="exp-gallery" wide />
              <Input label="Highlights (comma separated)" value={form.highlights} onChange={(v) => setForm({ ...form, highlights: v })} testid="exp-highlights" wide />
              <Area label="Short description" value={form.description} onChange={(v) => setForm({ ...form, description: v })} testid="exp-desc" />
              <Area label="Long description" value={form.long_description} onChange={(v) => setForm({ ...form, long_description: v })} testid="exp-longdesc" />
            </div>
            <div className="flex gap-6 mt-4">
              <label className="flex items-center gap-2 text-sm text-foreground"><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="exp-active" /> Visible</label>
              <label className="flex items-center gap-2 text-sm text-foreground"><input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} data-testid="exp-featured" /> Featured</label>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setEditing(null)} className="rounded-md px-4 py-2 text-sm font-medium border border-primary/15 hover:bg-secondary">Cancel</button>
              <button onClick={save} disabled={busy} data-testid="save-experience" className="inline-flex items-center gap-2 rounded-md bg-accent text-accent-foreground px-5 py-2 text-sm font-semibold disabled:opacity-60">{busy && <Loader2 size={15} className="animate-spin" />} Save</button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

function Input({ label, value, onChange, type = "text", testid, wide }) {
  return (
    <label className={`block ${wide ? "sm:col-span-2" : ""}`}>
      <span className="text-sm font-medium text-foreground/80 mb-1 block">{label}</span>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="w-full h-11 rounded-lg border border-input bg-transparent px-3 text-sm focus:ring-2 focus:ring-accent focus:outline-none" />
    </label>
  );
}
function Sel({ label, value, onChange, options, testid }) {
  return (
    <label className="block">
      <span className="text-sm font-medium text-foreground/80 mb-1 block">{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="w-full h-11 rounded-lg border border-input bg-transparent px-3 text-sm focus:ring-2 focus:ring-accent focus:outline-none">
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </label>
  );
}
function Area({ label, value, onChange, testid }) {
  return (
    <label className="block sm:col-span-2">
      <span className="text-sm font-medium text-foreground/80 mb-1 block">{label}</span>
      <textarea value={value} onChange={(e) => onChange(e.target.value)} rows={2} data-testid={testid}
        className="w-full rounded-lg border border-input bg-transparent px-3 py-2 text-sm focus:ring-2 focus:ring-accent focus:outline-none" />
    </label>
  );
}
